// Shared constants/helpers for the accountant Payroll workspace (Phase A/1).
export const ADJUSTMENT_CATEGORIES = [
  { key: "TIME", label: "Time / Hours", action: "edit_hours" },
  { key: "COMMISSION", label: "Commission", action: "edit_commissions" },
  { key: "BONUS", label: "Bonus", action: "edit_bonuses" },
  { key: "SPIFF", label: "Spiff", action: "edit_bonuses" },
  { key: "PTO", label: "PTO", action: "add_adjustments" },
  { key: "SICK", label: "Sick Pay", action: "add_adjustments" },
  { key: "HOLIDAY", label: "Holiday Pay", action: "add_adjustments" },
  { key: "SALARY", label: "Salary", action: "add_adjustments" },
  { key: "PIECE_RATE", label: "Piece Rate", action: "add_adjustments" },
  { key: "OTHER_EARNING", label: "Other Earning", action: "add_adjustments" },
  { key: "DEDUCTION", label: "Deduction", action: "add_adjustments" },
  { key: "OTHER", label: "Other", action: "add_adjustments" },
];

export const STATUS_STYLES = {
  OPEN: { label: "Open", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  REVIEW: { label: "Review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  APPROVED: { label: "Approved", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  LOCKED: { label: "Locked", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};

export const PAY_FREQUENCIES = [
  { key: "weekly", label: "Weekly", periodsPerYear: 52 },
  { key: "biweekly", label: "Biweekly", periodsPerYear: 26 },
  { key: "semimonthly", label: "Semimonthly", periodsPerYear: 24 },
  { key: "monthly", label: "Monthly", periodsPerYear: 12 },
];

/** Annual salary ÷ pay-periods-per-year (flat regardless of the selected
 * payroll period's exact length — per product decision). */
export function salaryPerPeriod(annualSalary, payFrequency) {
  const freq = PAY_FREQUENCIES.find((f) => f.key === payFrequency) || PAY_FREQUENCIES[1];
  return Math.round(((Number(annualSalary) || 0) / freq.periodsPerYear) * 100) / 100;
}

export function money2(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function signedMoney(n) {
  const v = Number(n) || 0;
  if (v === 0) return money2(0);
  return `${v > 0 ? "+" : "-"}${money2(Math.abs(v))}`;
}

/** Mirrors backend `has_payroll_permission` in routes/permissions.py. */
export function makeCanPayroll(isAdminPerm, permissions) {
  const perms = permissions || [];
  return (action) => {
    if (isAdminPerm) return true;
    if (perms.includes("payroll.manage")) return true;
    if (action === "view") return perms.includes("payroll.view");
    return perms.includes(`payroll.${action}`);
  };
}

/** Safely extract a renderable error string — a FastAPI 422 `detail` can be
 * a list of validation-error objects, which must never be passed to a toast
 * or JSX directly (React throws "Objects are not valid as a React child"). */
export function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

/** Product/service-aware commission cascade: technician default -> product
 * default % (pricebook item) -> technician-specific product % (most specific).
 * Falls back to the job's existing SYSTEM commission (unchanged, from the
 * live calc engine) for any line with no product-level rule on file. */
export function suggestedCommissionForJob(job, invoice, pay, pricebookById) {
  const lineItems = (invoice?.lineItems || []).filter((li) => li.pricebookId);
  if (lineItems.length === 0) return { pct: job.commissionPct, amount: job.commissionAmount, hasProductRule: false };
  const productOverrides = pay?.productOverrides || [];
  let amount = 0;
  let hasProductRule = false;
  lineItems.forEach((li) => {
    const lineTotal = (Number(li.qty) || 0) * (Number(li.unitPrice) || 0);
    const pb = pricebookById.get(li.pricebookId);
    const override = productOverrides.find((o) => o.pricebookId === li.pricebookId);
    let pct = job.commissionPct;
    if (override) { pct = override.pct; hasProductRule = true; }
    else if (pb && pb.commissionPct != null) { pct = pb.commissionPct; hasProductRule = true; }
    amount += lineTotal * (pct / 100);
  });
  const untracked = (job.invoiceAmount || 0) - lineItems.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unitPrice) || 0), 0);
  if (untracked > 0) amount += untracked * ((job.commissionPct || 0) / 100);
  const pct = job.invoiceAmount ? (amount / job.invoiceAmount) * 100 : job.commissionPct;
  return { pct: Math.round(pct * 100) / 100, amount: Math.round(amount * 100) / 100, hasProductRule };
}

/** TIME-category adjustments are stored in HOURS (not dollars) — convert
 * using the employee's own hourly rate before folding into a dollar total. */
export function adjustmentDollarTotal(list, row) {
  const rate = (row?.pay?.model === "hourly" || row?.pay?.model === "hybrid") ? (row.pay.hourlyRate || 0) : 0;
  const total = (list || []).reduce((s, a) => s + (a.category === "TIME" ? a.adjustmentAmount * rate : a.adjustmentAmount), 0);
  return Math.round(total * 100) / 100;
}

/** Pure predicate mirroring CommissionsTab's per-job "needs reversal" rule —
 * a Canceled/refunded job whose system commission was never reversed. */
export function needsCommissionReversal(jl, invoice, jobAdjustments) {
  const hasReversal = (jobAdjustments || []).some((a) => a.source === "reversal");
  const systemAmount = jl.included ? Number(jl.commissionAmount) || 0 : 0;
  return !jl.isSynthetic && (jl.status === "Canceled" || invoice?.payments?.some((p) => p.method === "refund")) && !hasReversal && systemAmount !== 0;
}

export const PAY_MODEL_LABELS = {
  hourly: "Hourly", commission: "Commission", hybrid: "Hybrid", flat: "Flat Rate", piece_rate: "Piece Rate", salary: "Salary",
};

/** True when a technician's pay model requires a rate that hasn't been set. */
export function isMissingPayRate(pay) {
  if (!pay) return true;
  if ((pay.model === "hourly" || pay.model === "hybrid") && !(pay.hourlyRate > 0)) return true;
  if ((pay.model === "commission" || pay.model === "hybrid") && !(pay.commissionPct > 0)) return true;
  if (pay.model === "salary" && !(pay.annualSalary > 0)) return true;
  if (pay.model === "flat" && !(pay.flatRatePerJob > 0)) return true;
  return false;
}

/** Exceptions dashboard (P5): one pass per row producing hard ("Needs Review")
 * and soft ("Warning") flags from data already loaded by the Payroll workspace
 * — no extra API calls beyond the bulk /timeclock/hours response. */
export function computeRowExceptions(row, { invoiceByJobId, adjustments, missingClockOutByEmail, missingClockOutByUserId }) {
  const hard = [];
  const soft = [];

  if (isMissingPayRate(row.pay)) hard.push({ key: "missing_pay_rate", label: "Missing pay rate" });

  const commissionIssueJobs = (row.jobLines || []).filter((jl) => {
    const invoice = invoiceByJobId.get(jl.jobId);
    const jobAdjustments = (adjustments || []).filter((a) => a.category === "COMMISSION" && a.jobId === jl.jobId);
    return needsCommissionReversal(jl, invoice, jobAdjustments);
  });
  if (commissionIssueJobs.length > 0) {
    hard.push({ key: "commission_issue", label: `${commissionIssueJobs.length} commission issue${commissionIssueJobs.length > 1 ? "s" : ""} — reversal needed` });
  }

  const missingClockOuts = missingClockOutByEmail?.[(row.tech.email || "").toLowerCase()] ?? missingClockOutByUserId?.[row.tech.userId] ?? 0;
  if (missingClockOuts > 0) hard.push({ key: "missing_clockout", label: `${missingClockOuts} missing clock-out${missingClockOuts > 1 ? "s" : ""}` });

  if (row.otHours > 0) soft.push({ key: "ot_hours", label: `${row.otHours.toFixed(2)} OT hours` });

  if (row.gpsHours > 0 && row.clockedHours > 0 && Math.abs(row.gpsHours - row.clockedHours) > 1) {
    soft.push({ key: "gps_time_conflict", label: "GPS/time conflict" });
  }

  const unapprovedCount = (adjustments || []).filter((a) => !a.approvedBy).length;
  if (unapprovedCount > 0) soft.push({ key: "unapproved_adjustment", label: `${unapprovedCount} unapproved adjustment${unapprovedCount > 1 ? "s" : ""}` });

  return { hard, soft };
}

/** Frozen per-employee snapshot sent when a period moves OPEN -> REVIEW. */
export function buildSnapshotRows(rows, titleById) {
  return rows.map((r) => ({
    employeeId: r.tech.id,
    techName: r.tech.name,
    jobTitle: titleById.get(r.tech.jobTitleId) || null,
    model: r.pay.model,
    jobsCount: r.jobsCount,
    totalHours: r.hoursUsed,
    regularHours: r.regularHours,
    otHours: r.otHours,
    regularPay: r.regularPay,
    otPay: r.otPay,
    commission: r.commission,
    flatPay: r.flatPay,
    salaryPay: r.salaryPay,
    bonus: r.bonus,
    total: r.total,
  }));
}
